import type { ReactNode } from 'react';
import { ApiError } from './api/client';
import { presentError } from './lib/error-messages';
import { pesanTanpaJargon } from './lib/pesan-tanpa-jargon';

export function LoadingState({ label }: { readonly label: string }): ReactNode {
  return (
    <section className="card" aria-busy="true">
      <p>{label}</p>
    </section>
  );
}

export function EmptyState({
  title,
  hint,
}: {
  readonly title: string;
  readonly hint: string;
}): ReactNode {
  return (
    <section className="card">
      <h2>{title}</h2>
      <p className="muted">{hint}</p>
    </section>
  );
}

/**
 * Kartu galat — membedakan penyebab, bukan menyeragamkan semuanya menjadi "terjadi kesalahan".
 *
 * Perbedaan ini penting bagi pengguna lapangan: "tidak berhak" menuntut menghubungi admin,
 * sedangkan "layanan tidak tersedia" menuntut mencoba lagi nanti. Menyeragamkannya membuat
 * orang mengulang tindakan yang tidak akan pernah berhasil.
 */
export function ErrorCard({
  error,
  onRetry,
  onSignOut,
}: {
  readonly error: ApiError;
  readonly onRetry?: () => void;
  readonly onSignOut?: () => void;
}): ReactNode {
  const presented = presentError(error.code, error.status);
  return (
    <section className="card error" role="alert">
      <h2>{presented.title}</h2>
      {/*
        Pesan server dibersihkan dari pengenal internal sebelum tampil. Lihat alasannya di
        `pesan-tanpa-jargon.ts` — singkatnya, "(workforce.worker.read)" bukan penjelasan bagi
        kurir, dan kalimat di bawahnya sudah menyebut tindakan yang benar.
      */}
      <p>{pesanTanpaJargon(error.message)}</p>
      <p className="muted small">{presented.hint}</p>
      {error.correlationId ? (
        <p className="muted small">Kode rujukan: {error.correlationId}</p>
      ) : null}
      <div className="row">
        {presented.canRetry ? (
          <button type="button" onClick={onRetry ?? (() => window.location.reload())}>
            Coba lagi
          </button>
        ) : null}
        {onSignOut ? (
          <button type="button" onClick={onSignOut}>
            Keluar
          </button>
        ) : null}
      </div>
    </section>
  );
}

/** Nama lama dipertahankan agar pemanggil Gate B tidak perlu diubah serentak. */
export const ErrorState = ErrorCard;
